import { AnimatePresence, motion } from 'framer-motion';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';

import { TaskPanelSkeleton } from '@/components/ui/skeletons/MatterSkeleton';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import {
  useGetTaskQuery,
  useGetTaskStatusesQuery,
} from '@/services/api';
import { TaskStatus } from '@/services/types/client-intake-types';
import { CanonicalTaskStatus } from '@/services/types/task-types';
import { filterTaskStatusesByGroup } from '@/utils/taskStatusUtils';

import { useIsNarrowDesktop } from '../../hooks/use-mobile';
import useMatterSelection from '../../hooks/useMatterSelection';
import { useLazyGetPortalLinkQuery } from '../../services/api/clientPortalService';
import { useGetStaffNotesQuery } from '../../services/api/staffNotesService';
import { MatterStatus } from '../../services/types/client-intake-types';
import { ActivityPanel } from '../activity-panel/ActivityPanel';
import { NotesActivityPanel } from '../activity-panel/NotesActivityPanel';
import { AgentChatDrawer } from '../agent-chat/AgentChatDrawer';
import { AgentChatProvider } from '../agent-chat/context/AgentChatContext';
import { CommandPalette, CommandPaletteProvider } from '../command-palette';
import { MakeCallModal } from '../common/calls/make-call-modal';
import { EscalateCaseModal } from '../common/EscalateCaseModal';
import { ScheduleMeetingModal } from '../common/ScheduleMeetingModal';
import { MatterActionsProvider } from '../contexts/MatterActionsContext';
import { useNotification } from '../contexts/NotificationContext';
import { DocumentExplorerStateProvider } from '../documents-v2/context/DocumentExplorerStateContext';
import { DumbDataCollection } from '../dumb-data-collection';
import FrontEmailDialog from '../matter/SendFrontEmailDialog';
import UpdatesView from '../matter/UpdatesView';
import { useFirmsRetrieveQuery } from '@/services/api/generated/firmsService';
import { NotesPanel, PinnedNotesBar } from '../notes-panel';
import { PropertyLookupDrawer } from '../property-lookup/PropertyLookupDrawer';
import { ShareDialog } from '../share/ShareDialog';
import { CreateImmediateTaskModal } from '../tasks/CreateImmediateTaskModal';
import { CreateTaskModal } from '../tasks/CreateTaskModal';
import MatterPanel from './MatterPanel';
import { MatterRightActionBar, RightPanelType } from './MatterRightActionBar';
import { MatterRightPanel } from './MatterRightPanel';
import { MatterSideNav } from './MatterSideNav';
import MatterTopNav from './MatterTopNav';

// Prefill state types
interface EmailPrefill {
  to?: string[];
  subject?: string;
  body?: string;
}

interface CallPrefill {
  phoneNumber?: string;
  contactLabel?: string;
}

const MatterLayout: React.FC = () => {
  const { selectedMatterData, selectedMatterLoading, matter_id } =
    useMatterSelection();

  const firmId = selectedMatterData?.firm?.id;
  const { data: firmData } = useFirmsRetrieveQuery(
    { id: firmId ?? '' },
    { skip: !firmId },
  );
  const isMiniTortMatter = firmData?.is_mini_tort === true;

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNarrowDesktop = useIsNarrowDesktop();
  const notify = useNotification();
  const [getPortalLink] = useLazyGetPortalLinkQuery();
  const captureEvent = usePostHogCapture();
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [activeRightPanel, setActiveRightPanel] =
    useState<RightPanelType>('tasks');
  const [notesActivityDefaultFilter, setNotesActivityDefaultFilter] = useState<
    'notes' | 'activity'
  >('notes');
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isImmediateTaskModalOpen, setIsImmediateTaskModalOpen] =
    useState(false);
  const [isSidebarPinned, setIsSidebarPinned] = useState(true);
  const sidebarPinnedBeforeDocsRef = useRef(true);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [initialChatVersion, setInitialChatVersion] = useState<'agent' | 'agent_v2' | null>(null);
  const [initialThreadId, setInitialThreadId] = useState<string | null>(null);
  const [isDialpadOpen, setIsDialpadOpen] = useState(false);
  const [isFrontEmailOpen, setIsFrontEmailOpen] = useState(false);
  const [isPropertyLookupOpen, setIsPropertyLookupOpen] = useState(false);
  const [isEscalateOpen, setIsEscalateOpen] = useState(false);
  const [isScheduleMeetingOpen, setIsScheduleMeetingOpen] = useState(false);

  // Prefill state for modals
  const [emailPrefill, setEmailPrefill] = useState<EmailPrefill | null>(null);
  const [callPrefill, setCallPrefill] = useState<CallPrefill | null>(null);

  // Check for pinned notes to force-show sidebar
  const { data: staffNotes } = useGetStaffNotesQuery(
    { matterId: matter_id || '' },
    {
      skip: !matter_id,
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );
  const hasPinnedNotes = useMemo(
    () => (staffNotes || []).some((n) => n.is_pinned),
    [staffNotes],
  );
  const [isPinnedNotesExpanded, setIsPinnedNotesExpanded] = useState(true);

  // Right panel resize state
  const PANEL_MIN_WIDTH = 320;
  const PANEL_MAX_WIDTH = 600;
  const PANEL_DEFAULT_WIDTH = 380;
  const PANEL_COLLAPSE_THRESHOLD = 200; // Width at which panel will close
  const PANEL_STORAGE_KEY = 'matter-right-panel-width';

  const [panelWidth, setPanelWidth] = useState(() => {
    const stored = localStorage.getItem(PANEL_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : PANEL_DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isNearCollapse, setIsNearCollapse] = useState(false);
  const [currentDragWidth, setCurrentDragWidth] = useState<number | null>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const dragWidthRef = useRef<number | null>(null);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = panelWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        // Dragging left increases width, dragging right decreases width
        const delta = startXRef.current - moveEvent.clientX;
        const newWidth = startWidthRef.current + delta;
        // Allow dragging below minimum to enable collapse gesture
        const clampedWidth = Math.min(Math.max(newWidth, 0), PANEL_MAX_WIDTH);

        dragWidthRef.current = clampedWidth;
        setCurrentDragWidth(clampedWidth);
        setIsNearCollapse(clampedWidth < PANEL_COLLAPSE_THRESHOLD);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        const finalWidth = dragWidthRef.current ?? startWidthRef.current;

        // Close panel if dragged below threshold
        if (finalWidth < PANEL_COLLAPSE_THRESHOLD) {
          setActiveRightPanel(null);
        } else {
          // Clamp to valid range and persist
          const validWidth = Math.min(
            Math.max(finalWidth, PANEL_MIN_WIDTH),
            PANEL_MAX_WIDTH,
          );
          setPanelWidth(validWidth);
          localStorage.setItem(PANEL_STORAGE_KEY, validWidth.toString());
        }

        dragWidthRef.current = null;
        setCurrentDragWidth(null);
        setIsNearCollapse(false);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // Prevent text selection during resize
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [panelWidth],
  );

  // Calculate the displayed width during drag
  const displayedPanelWidth =
    isResizing && currentDragWidth !== null
      ? Math.max(currentDragWidth, 0)
      : panelWidth;

  // Handlers for opening modals with prefilled values
  const handleOpenEmailWithPrefill = (prefill?: EmailPrefill) => {
    setEmailPrefill(prefill || null);
    setIsFrontEmailOpen(true);
  };

  const handleCloseEmail = () => {
    setIsFrontEmailOpen(false);
    setEmailPrefill(null);
  };

  const handleOpenCallWithPrefill = (prefill?: CallPrefill) => {
    setCallPrefill(prefill || null);
    setIsDialpadOpen(true);
  };

  const handleCloseCall = () => {
    setIsDialpadOpen(false);
    setCallPrefill(null);
  };

  const handleCopyPortalLink = async (): Promise<string> => {
    if (!matter_id) {
      notify('No matter selected', 'error', 3000);
      throw new Error('No matter selected');
    }
    const result = await getPortalLink(matter_id).unwrap();
    captureEvent('copy_portal_link', { page: 'matter', component: 'sidebar_action_bar' });
    try {
      await navigator.clipboard.writeText(result.portal_url);
      notify('Secure upload link copied to clipboard', 'success', 3000);
    } catch {
      // Clipboard API failed — popover will show the link for manual copy
    }
    return result.portal_url;
  };

  // Auto-collapse right panel on narrow screens
  useEffect(() => {
    if (isNarrowDesktop) {
      setActiveRightPanel(null);
    }
  }, [isNarrowDesktop]);

  // Handle deep linking to FinchGPT conversations
  // Supports: ?finchgpt=<thread_id>&chat_version=<agent|agent_v2>
  const finchgptDeeplinkProcessedRef = useRef(false);
  useEffect(() => {
    if (finchgptDeeplinkProcessedRef.current) return;
    const finchgptThreadId = searchParams.get('finchgpt');
    if (finchgptThreadId) {
      finchgptDeeplinkProcessedRef.current = true;
      const chatVersion = searchParams.get('chat_version') === 'agent_v2' ? 'agent_v2' : 'agent';
      setInitialChatVersion(chatVersion);
      setInitialThreadId(finchgptThreadId);
      setIsAssistantOpen(true);

      // Clean up the deeplink params from the URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('finchgpt');
      newParams.delete('chat_version');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle deep linking to right panel tabs and specific items
  // Supports: ?tab=notes, ?tab=activity, ?note_id=xyz, ?event_id=xyz
  // Consume the tab param after applying it so it doesn't re-fire on every navigation
  // note_id/event_id are consumed by child components (NotesPanel, EventList) after scroll+highlight
  const lastProcessedItemRef = useRef<string | null>(null);
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const noteId = searchParams.get('note_id');
    const eventId = searchParams.get('event_id');

    // Determine which tab to open based on params
    let targetTab: 'notes' | 'activity' | null = null;
    if (tabParam === 'activity' || (!tabParam && eventId)) {
      targetTab = 'activity';
    } else if (tabParam === 'notes' || (!tabParam && noteId)) {
      targetTab = 'notes';
    }

    if (targetTab) {
      // For item-level deep links (note_id/event_id) without tab param,
      // only process once per unique ID to avoid forcing the panel back open on every nav
      const itemId = noteId || eventId;
      if (!tabParam && itemId) {
        if (lastProcessedItemRef.current === itemId) return;
        lastProcessedItemRef.current = itemId;
      }

      setNotesActivityDefaultFilter(targetTab);
      setActiveRightPanel('notes-activity');

      // Consume the tab param; note_id/event_id are consumed by child components
      if (tabParam) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('tab');
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  // Derive active tab from current URL path
  const activeTab = useMemo(() => {
    const pathParts = location.pathname.split('/');
    // Assumes path is /matters/:matter_id/:tab?
    // pathParts[3] will be the tab name or undefined
    const currentTab = pathParts[3] || 'home';
    const validTabs = [
      'home',
      'services',
      'documents',
      'artifacts',
      'treatment',
      'workspaces',
      'deliverables',
      'coverage',
      'risk',
      'ledger',
      'case-expenses',
      'police',
      'updates',
      'mail',
      'fax',
      'demand',
      'document-review',
      'client-comms',
      'correspondence',
      'cms-sync',
    ];
    return validTabs.includes(currentTab) ? currentTab : 'home';
  }, [location.pathname]);

  // Auto-collapse sidebar on documents tab to maximize preview space
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    if (activeTab === 'documents' && prevActiveTabRef.current !== 'documents') {
      sidebarPinnedBeforeDocsRef.current = isSidebarPinned;
      setIsSidebarPinned(false);
    } else if (activeTab !== 'documents' && prevActiveTabRef.current === 'documents') {
      setIsSidebarPinned(sidebarPinnedBeforeDocsRef.current);
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab, isSidebarPinned]);

  // Detect correspondence package editing (sub-route with a package_id)
  const isCorrespondencePackageEdit =
    activeTab === 'correspondence' &&
    /\/correspondence\/.+/.test(location.pathname);

  // Show pinned notes bar when expanded and screen/context allows it
  const showPinnedNotesBar =
    hasPinnedNotes &&
    isPinnedNotesExpanded &&
    !isNarrowDesktop &&
    !isCorrespondencePackageEdit;

  // Auto-collapse right panel when entering correspondence package editor
  const prevCorrespondenceEditRef = useRef(false);
  useEffect(() => {
    if (isCorrespondencePackageEdit && !prevCorrespondenceEditRef.current) {
      setActiveRightPanel(null);
    } else if (!isCorrespondencePackageEdit && prevCorrespondenceEditRef.current) {
      setActiveRightPanel('tasks');
    }
    prevCorrespondenceEditRef.current = isCorrespondencePackageEdit;
  }, [isCorrespondencePackageEdit]);

  const handleTabChange = (newValue: string) => {
    if (selectedMatterLoading || !selectedMatterData?.id) return;

    // Preserve existing search params (e.g., task_id) so the right panel state stays intact
    // but clean up deep-link item params that are only relevant for the initial navigation
    const params = new URLSearchParams(searchParams);
    params.delete('note_id');
    params.delete('event_id');
    const search = params.toString();

    const path =
      newValue === 'home'
        ? `/matters/${selectedMatterData.id}`
        : `/matters/${selectedMatterData.id}/${newValue}`;

    navigate(search ? `${path}?${search}` : path);
  };

  const handleOpenCreateTask = () => {
    setIsCreateTaskModalOpen(true);
    // Open tasks panel
    setActiveRightPanel('tasks');
  };

  // Check if DDC should be shown
  const showDDC = searchParams.get('showDDC') === 'true';

  // Determine if right action bar should be shown
  const showRightActionBar = activeTab !== 'workspaces' && !showDDC;

  // Check if case is in initial review to disable escalation
  const isInInitialReview =
    selectedMatterData?.status === MatterStatus.INITIAL_REVIEW;

  // Fetch task data when viewing a specific task in the right panel
  const currentTaskId = searchParams.get('task_id');
  const { data: currentTask } = useGetTaskQuery(
    { taskId: currentTaskId!, matterId: matter_id! },
    { skip: !currentTaskId || !matter_id },
  );
  const { data: taskStatuses } = useGetTaskStatusesQuery(undefined, {
    skip: !currentTaskId,
  });
  // Ref populated by TaskDetail with its internal handleStatusChange,
  // so the panel header delegates to TaskDetail's full validation logic
  // (change reasons, TRR attachment checks, external sync, CSAT, etc.)
  const taskDetailStatusChangeRef = useRef<((status: { id: string; canonical_status: string }) => void | Promise<void>) | null>(null);

  const handleTaskStatusChange = useCallback(
    async (status: TaskStatus) => {
      if (taskDetailStatusChangeRef.current) {
        await taskDetailStatusChangeRef.current(status);
      } else {
        toast.error('Please wait for task to load');
      }
    },
    [],
  );

  const handleCompleteTask = useCallback(async () => {
    if (!currentTask || !taskStatuses) return;
    const filteredStatuses = filterTaskStatusesByGroup(
      taskStatuses,
      currentTask,
    );
    const completedStatus =
      filteredStatuses.find(
        (s) =>
          s.canonical_status === CanonicalTaskStatus.COMPLETED &&
          (s.name.toLowerCase() === 'completed' ||
            s.name.toLowerCase() === 'complete'),
      ) ||
      filteredStatuses.find(
        (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
      );
    if (completedStatus) {
      await handleTaskStatusChange(completedStatus);
    }
  }, [currentTask, taskStatuses, handleTaskStatusChange]);

  // Task search state (lifted up for header search icon)
  const [taskSearchTerm, setTaskSearchTerm] = React.useState('');

  // Render the right panel content based on active panel
  const renderRightPanelContent = () => {
    switch (activeRightPanel) {
      case 'tasks':
        return selectedMatterLoading ? (
          <TaskPanelSkeleton />
        ) : (
          <ActivityPanel
            activeTab="tasks"
            onOpenCreateTask={handleOpenCreateTask}
            onStatusChangeRef={taskDetailStatusChangeRef}
            searchTerm={taskSearchTerm}
            onSearchTermChange={setTaskSearchTerm}
          />
        );
      case 'notes-activity':
        return selectedMatterLoading ? (
          <TaskPanelSkeleton />
        ) : (
          <NotesActivityPanel
            defaultFilter={notesActivityDefaultFilter}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AgentChatProvider>
      <CommandPaletteProvider>
        <MatterActionsProvider
          openEmailModal={handleOpenEmailWithPrefill}
          openCallModal={handleOpenCallWithPrefill}
        >
          <div className="flex flex-col h-full w-full">
            {selectedMatterData && (
              <ShareDialog
                open={isShareDialogOpen}
                onClose={() => setIsShareDialogOpen(false)}
                matterId={selectedMatterData.id}
              />
            )}

            {/* Top Header */}
            <div className="bg-white flex-shrink-0">
              <MatterTopNav matterId={selectedMatterData?.id} />
            </div>

            {/* Content Area with Sidebar */}
            <div className="flex flex-1 min-h-0">
              {/* Left Sidebar Navigation */}
              <MatterSideNav
                matterId={matter_id || ''}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                isLoading={selectedMatterLoading}
                isPinned={isSidebarPinned}
                onTogglePin={() => setIsSidebarPinned(!isSidebarPinned)}
                isMiniTort={isMiniTortMatter}
              />

              {/* Main content with loading state */}
              <div
                className={cn(
                  'h-full transition-all duration-300 ease-in-out',
                  activeTab === 'workspaces' || showDDC
                    ? 'w-full'
                    : 'flex-1 overflow-y-auto',
                )}
              >
                <DocumentExplorerStateProvider>
                  {activeTab === 'updates' ? <UpdatesView /> : <MatterPanel />}
                </DocumentExplorerStateProvider>
              </div>

              {/* DDC sidebar */}
              {showDDC && (
                <div className="w-[400px] max-w-[400px] overflow-y-auto border-l-2 border-amber-400 h-full transition-all duration-300 ease-in-out flex-shrink-0 bg-white shadow-lg">
                  <DumbDataCollection onClose={() => {}} />
                </div>
              )}

              {/* Right Action Bar and Panel Container */}
              {showRightActionBar && (
                <div className="flex h-full flex-shrink-0">
                  {/* Panel Card - aligned with first button */}
                  <AnimatePresence mode="wait">
                    {(activeRightPanel || showPinnedNotesBar) && (
                      <motion.div
                        key="right-panel"
                        className="pt-4 pr-1 h-full flex overflow-hidden"
                        initial={{ width: 0, opacity: 0 }}
                        animate={{
                          width: displayedPanelWidth + 12, // +12 for resize handle
                          opacity: 1,
                        }}
                        exit={{ width: 0, opacity: 0 }}
                        transition={
                          isResizing
                            ? { duration: 0 }
                            : { type: 'spring', stiffness: 400, damping: 35 }
                        }
                      >
                        {/* Resize Handle - matches panel card height */}
                        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                        <div
                          onMouseDown={handleResizeMouseDown}
                          className="w-3 h-[calc(100%-2rem)] cursor-col-resize flex-shrink-0 flex items-center justify-center py-4"
                        >
                          <motion.div
                            className="w-0.5 h-full rounded-full"
                            animate={{
                              backgroundColor: isResizing
                                ? 'rgb(161, 161, 170)' // zinc-400
                                : 'rgb(212, 212, 216)', // zinc-300
                            }}
                            whileHover={{
                              backgroundColor: 'rgb(161, 161, 170)', // zinc-400
                            }}
                            transition={{ duration: 0.15 }}
                          />
                        </div>
                        <div
                          className={cn(
                            'relative flex-shrink-0 flex flex-col',
                            activeRightPanel
                              ? 'h-[calc(100%-2rem)]'
                              : 'h-auto',
                          )}
                          style={{ width: displayedPanelWidth }}
                        >
                          {/* Overlay when near collapse - only when a panel is active */}
                          {activeRightPanel && (
                            <motion.div
                              className="absolute inset-0 rounded-xl z-50 pointer-events-none bg-white/80 backdrop-blur-sm"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: isNearCollapse ? 1 : 0 }}
                              transition={{ duration: 0.15 }}
                            />
                          )}

                          {/* Pinned notes bar - shown when expanded */}
                          <AnimatePresence>
                            {showPinnedNotesBar && matter_id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  type: 'spring',
                                  stiffness: 400,
                                  damping: 30,
                                }}
                                className={cn(
                                  'flex-shrink-0 bg-white border border-zinc-200 overflow-hidden',
                                  activeRightPanel
                                    ? 'rounded-t-xl border-b-0'
                                    : 'rounded-xl',
                                )}
                              >
                                <PinnedNotesBar
                                  matterId={matter_id}
                                  onMinimize={() =>
                                    setIsPinnedNotesExpanded(false)
                                  }
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* Regular panel content */}
                          {activeRightPanel && (
                            <MatterRightPanel
                              panelType={activeRightPanel}
                              onClose={() => setActiveRightPanel(null)}
                              onAdd={
                                activeRightPanel === 'tasks'
                                  ? handleOpenCreateTask
                                  : undefined
                              }
                              onBack={
                                activeRightPanel === 'tasks' &&
                                searchParams.get('task_id')
                                  ? () => {
                                      const newSearchParams =
                                        new URLSearchParams(searchParams);
                                      newSearchParams.delete('task_id');
                                      setSearchParams(newSearchParams);
                                    }
                                  : undefined
                              }
                              taskDetailHeaderProps={
                                activeRightPanel === 'tasks' && currentTask
                                  ? {
                                      task: currentTask,
                                      onStatusChange: handleTaskStatusChange,
                                      onComplete: handleCompleteTask,
                                    }
                                  : undefined
                              }
                              searchProps={
                                activeRightPanel === 'tasks'
                                  ? {
                                      value: taskSearchTerm,
                                      onChange: setTaskSearchTerm,
                                    }
                                  : undefined
                              }
                              openInTasksPageUrl={
                                activeRightPanel === 'tasks' && matter_id
                                  ? `/tasks?matter=${matter_id}`
                                  : undefined
                              }
                              className={cn(
                                'flex-1 min-h-0 w-full',
                                showPinnedNotesBar
                                  ? 'rounded-b-xl rounded-t-none'
                                  : '',
                              )}
                            >
                              {renderRightPanelContent()}
                            </MatterRightPanel>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Action Buttons */}
                  <MatterRightActionBar
                    activePanel={activeRightPanel}
                    onPanelChange={setActiveRightPanel}
                    onOpenAssistant={() => setIsAssistantOpen(true)}
                    onOpenDialpad={() => handleOpenCallWithPrefill()}
                    onOpenSendEmail={() => handleOpenEmailWithPrefill()}
                    onOpenPropertyLookup={() => setIsPropertyLookupOpen(true)}
                    onCopyPortalLink={handleCopyPortalLink}
                    onOpenScheduleMeeting={() => setIsScheduleMeetingOpen(true)}
                    onOpenEscalate={() => setIsEscalateOpen(true)}
                    isEscalateDisabled={isInInitialReview}
                    hasPinnedNotes={hasPinnedNotes}
                    isPinnedNotesExpanded={isPinnedNotesExpanded}
                    onTogglePinnedNotes={() =>
                      setIsPinnedNotesExpanded((prev) => !prev)
                    }
                  />
                </div>
              )}
            </div>

            {/* Create Task Modal */}
            <CreateTaskModal
              isOpen={isCreateTaskModalOpen}
              onClose={() => setIsCreateTaskModalOpen(false)}
              defaultMatterId={matter_id}
              defaultMatterName={selectedMatterData?.name}
            />

            {/* Immediate Task Modal */}
            <CreateImmediateTaskModal
              isOpen={isImmediateTaskModalOpen}
              onClose={() => setIsImmediateTaskModalOpen(false)}
            />

            {/* Global Command Palette */}
            <CommandPalette />

            {/* Dialpad Modal */}
            <MakeCallModal
              isOpen={isDialpadOpen}
              onClose={handleCloseCall}
              initialPhoneNumber={callPrefill?.phoneNumber}
              initialContactLabel={callPrefill?.contactLabel}
              source="matter_layout"
            />

            {/* Front Email Dialog */}
            {selectedMatterData?.id && (
              <FrontEmailDialog
                matterId={selectedMatterData.id}
                open={isFrontEmailOpen}
                onClose={handleCloseEmail}
                defaultTo={emailPrefill?.to}
                defaultSubject={emailPrefill?.subject}
                defaultBody={emailPrefill?.body}
              />
            )}

            {/* Agent Chat Drawer */}
            {selectedMatterData?.id && (
              <AgentChatDrawer
                matterId={selectedMatterData.id}
                isOpen={isAssistantOpen}
                onClose={() => {
                  setIsAssistantOpen(false);
                  setInitialChatVersion(null);
                  setInitialThreadId(null);
                }}
                initialChatVersion={initialChatVersion}
                initialThreadId={initialThreadId}
              />
            )}

            {/* Property Lookup Drawer */}
            <PropertyLookupDrawer
              isOpen={isPropertyLookupOpen}
              onClose={() => setIsPropertyLookupOpen(false)}
            />

            {/* Escalate Case Modal */}
            {selectedMatterData?.id && (
              <EscalateCaseModal
                isOpen={isEscalateOpen}
                onClose={() => setIsEscalateOpen(false)}
                matterId={selectedMatterData.id}
                matterName={selectedMatterData.name || 'Unknown Matter'}
                matterStatus={selectedMatterData.status}
              />
            )}

            {/* Schedule Meeting Modal */}
            {selectedMatterData?.id && (
              <ScheduleMeetingModal
                isOpen={isScheduleMeetingOpen}
                onClose={() => setIsScheduleMeetingOpen(false)}
                matterId={selectedMatterData.id}
                matterName={selectedMatterData.name}
                clientState={selectedMatterData.state}
                clientLocation={selectedMatterData.incident_note?.location}
              />
            )}
          </div>
        </MatterActionsProvider>
      </CommandPaletteProvider>
    </AgentChatProvider>
  );
};

export default MatterLayout;
